import React, { useState, Fragment } from "react";
import Dialog from "@material-ui/core/Dialog";
import { useTheme } from '@material-ui/core/styles';
import DialogContent from "@material-ui/core/DialogContent";
import { useDispatch, useSelector } from "react-redux";
import { useSetState } from "Helpers/Hooks";
import { DialogActions, Grid } from "@material-ui/core";
import Input from "Components/Input/Input";
import Translator from "Languages/Utility/Translator";
import millimeterMask from "Components/Input/components/millimeterMask";
import * as R from "ramda";
import { read } from "Helpers/LocalStorage";
import ActionFormatter from "Components/Table/ActionFormatter/ActionFormatter";
import { EditFomMastik, GetAllFomMastik } from "Stores/actions";
import { toggleNFomMastik } from "Constants/Constants";
import showNotify from "Helpers/Notify";
import MuiButton from "Components/MuiButton/MuiButton";
import { useTranslation } from 'react-i18next';
import BackDropLoading from "Components/BackDropLoading/BackDropLoading";
import MuiRadioGroup from "Components/MuiRadioGroup/MuiRadioGroup";
import * as Icons from "react-bootstrap-icons";

function FomMastikRowEditor({ row }) {
  const { direction } = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { fomMastikDataSearch, fomMastikDataPagination } = useSelector(
    (state) => ({
      fomMastikDataSearch: state.fomMastik.fomMastikDataSearch,
      fomMastikDataPagination: state.fomMastik.fomMastikDataPagination,
    })
  );
  const [formValid, setFormValid] = useState({});
  const [showDialog, setShowDialog] = useState(false);
  const [change, setChange] = useState(false);
  const [loading, setLoading] = useState(false);
  const [controls, setControls] = useSetState({
    fomMastikType: {
      type: "toggle",
      arrayItems: toggleNFomMastik,
      value: toggleNFomMastik[0],
      validation: {
        required: true,
      },
      auth: true,
    },
    name: {
      type: "text",
      value: row.Name,
      validation: {
        required: true,
      },
      auth: true,
    },
    cover: {
      type: "millimeter",
      value: row.Cover,
      validation: {
        required: true,
        isNumeric: true,
        minLength: 1,
        maxLength: 3,
        min: 1,
      },
      auth: true,
    },
    groupPrice: {
      type: "price",
      value: row.GroupPrice,
      validation: {
        required: true,
        isPrice: true,
      },
      auth: read("StypeU") === "1",
    },
    userPrice: {
      type: "price",
      value: row.UserPrice,
      validation: {
        required: true,
        isPrice: true,
      },
      auth: read("StypeU") === "2" || read("StypeU") === "5",
    },
  });

  const handleDialog = () => {
    setShowDialog(!showDialog);
  }

  const setValue = (name, value, validation) => {
    setControls({ [name]: { value: value } });
    setFormValid((prev) => ({ ...prev, [name]: validation }));
    setChange(true);
  }

  const onChangeToggle = async (name, key, value) => {
    setControls({ [name]: { value: value } });
    setFormValid((prev) => ({ ...prev, [name]: true }));
    setChange(true);
  };

  const onFinish = async (e) => {
    e.preventDefault();
    setLoading(true);
    if(change) {
      const { name, groupPrice, cover, fomMastikType, userPrice } = controls;
      if (
        !R.isEmpty(formValid) &&
        Object.values(formValid).every((item) => item === true)
      ) {
        let price;
        if (groupPrice.auth) {
          price = +groupPrice.value;
        } else {
          price = +userPrice.value;
        }
        try {
          const data = {
            KeyGroup: read("groupKey"),
            KeyUser: read("userKey"),
            Id: +row.Id,
            IdType: +fomMastikType.value.value,
            Name: name.value,
            Cover: +cover.value,
            Price: price,
          };
          await dispatch(EditFomMastik(data));
          const params = {
            KeyGroup: read("groupKey"),
            KeyUser: read("userKey"),
            Name: fomMastikDataSearch?.Name,
            Limit: fomMastikDataPagination.Limit,
            Page: fomMastikDataPagination.Page,
          };
          await dispatch(GetAllFomMastik(params));
          onClear();
          handleDialog();
        } catch (e) {
          showNotify({
            text: t('correctFormDataRequired'),
            customClass: "error",
          });
        }
        handleDialog();
      } else if (R.isEmpty(formValid)) {
        handleDialog();
      } else {
        showNotify({
          text: t('correctFormDataRequired'),
          customClass: "error",
        });
      }
    } else {
      handleDialog();
    }
    setLoading(false);
  }

  const onEnter = async () => {
    onClear();
  }

  const onClear = () => {
    setFormValid({});
    setControls({ name: { value: row.Name } });
    setControls({ fomMastikType: { value: toggleNFomMastik.find(i => i.value === row.IdType) } });
    setControls({ cover: { value: row.Cover } });
    setControls({ groupPrice: { value: row.GroupPrice } });
    setControls({ userPrice: { value: row.UserPrice } });
  }

  return (
    <>
      <BackDropLoading loading={loading} />
      <ActionFormatter
        title={<Translator id="virayesh" />}
        icon={<Icons.Pencil size={17} />}
        className="warning-fill"
        onClick={handleDialog}
        disabled={
          (row.IsG &&
              read("StypeU") !== "1") ||
          read("fomMasticRules")?.accessUpdate === false ||
          row.Disable
        }
      />
      <Dialog
        dir={direction}
        open={showDialog}
        onClose={handleDialog}
        onEnter={onEnter}
        fullWidth
        maxWidth="xs"
      >
        <form onSubmit={onFinish}>
          <div className="dialogHeader">
            <p>
              <Translator id="virayesh_FomMastik" />
            </p>
          </div>
          <DialogContent>
            <Grid container spacing={2} className="d-flex mb-2">
              {
                Object.keys(controls).filter(key => controls[key].type === "toggle").map((key, index) => {
                  return (
                    <Grid item key={key + index} xs={12}>
                      <MuiRadioGroup
                        name={key}
                        label={<Translator id={key} />}
                        value={controls[key].value}
                        items={controls[key].arrayItems}
                        onChange={onChangeToggle}
                        translate={true}
                        className="horizontalRadio"
                      />
                    </Grid>
                  );
                })
              }
            </Grid>
            <Grid container spacing={2} className="d-flex">
              {
                Object.keys(controls).filter(key => controls[key].type !== "toggle" && controls[key].auth).map((key, index) => {
                  return (
                    <Grid item key={key + index} xs={12}>
                      <Input
                        setValue={setValue.bind(this)}
                        value={controls[key].value}
                        name={key}
                        placeholder={t(key)}
                        validation={controls[key].validation}
                        label={<Translator id={key} />}
                        type={controls[key].type}
                        inputComponent={controls[key].type === "millimeter" ? millimeterMask : ''}
                        endIcon={controls[key].type === "price" ? <Translator id='moneyUnit' /> : controls[key].type === "millimeter" ? <Translator id='millimeter' /> : ''}
                        minLength={controls[key]?.validation?.minLength}
                        maxlength={controls[key]?.validation?.maxLength}
                      />
                    </Grid>
                  );
                }
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <MuiButton
              color="primary"
              type="submit"
              title={<Translator id="button_save" />}
            />
            <MuiButton
              onClick={handleDialog}
              title={<Translator id="enseraf" />}
            />
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
}

export default FomMastikRowEditor;
